'use client';

import { useEffect, useState, useCallback } from 'react';
import { useEvent, useUnit } from 'effector-react';
import classNames from 'classnames';
import { ButtonSize } from '@nx-frontend/shared/ui/button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal';
import { usePreventUnload } from '@nx-frontend/shared/hooks';
import {
  $allFilesUploaded,
  $isUploadModalCollapsed,
  $uploadFileIds,
  $uploadProgress,
  cancelUploadingProgressFilesFx,
  clearFilesUploadingStoreEvent,
  toggleCollapseUploadModalEvent,
} from '@nx-frontend/shared/store/feature-files';
import { getBgClassNames, getBorderClassNames, getTextClassNames } from '@nx-frontend/shared/themes';
import {
  PopupType,
  PortalBgType,
  PortalBorderType,
  PortalStyleConfig,
  PortalTextType,
  ThemeColorSchemas,
} from '@nx-frontend/shared/types';
import { FileProgressListItem } from '../file-list-item';

interface FilesUploadModalProps {
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
}
export function FilesUploadModal({ themeColors, themeStyle }: FilesUploadModalProps) {
  const [
    ids,
    allFilesUploaded,
    isUploadModalCollapsed,
    clearFilesUploadingStore,
    openModal,
    closeModal,
    cancelUploadingProgressFiles,
  ] = useUnit([
    $uploadFileIds,
    $allFilesUploaded,
    $isUploadModalCollapsed,
    clearFilesUploadingStoreEvent,
    openModalEvent,
    closeModalEvent,
    cancelUploadingProgressFilesFx,
  ]);
  const [toggleCollapseUploadModal] = useEvent([toggleCollapseUploadModalEvent]);
  const [visible, setVisible] = useState(false);
  const [isListShortView, setIsListShortView] = useState(true);
  const [isListShortAnimationInProgress, setIsListShortAnimationInProgress] = useState(false);

  useEffect(() => {
    if (!visible && ids.length) {
      setVisible(true);
    }
    if (visible && !ids.length) {
      setVisible(false);
    }
  }, [ids, visible]);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  usePreventUnload(!allFilesUploaded, () => {});

  const showCancelConfirmModal = useCallback(() => {
    openModal({
      title: 'Are you sure you want to cancel files uploading?',
      content: PopupType.DIALOG,
      closeButtonSize: 18,
      modalContainerStyles: 'w-[435px]',
      payload: {
        payloadType: PopupType.DIALOG,
        text: 'File uploading isn’t finished. Cancel all current uploadings?',
        themeColors: themeColors,
        themeStyle: themeStyle,
        buttons: [
          {
            main: false,
            text: 'Cancel uploading',
            size: ButtonSize.MD,
            onClick: () => {
              cancelUploadingProgressFiles();
              closeModal();
            },
          },
          {
            main: true,
            size: ButtonSize.MD,
            text: 'Continue uploading',
          },
        ],
      },
    });
  }, [openModal, themeColors, themeStyle, cancelUploadingProgressFiles, closeModal]);

  const handleClose = useCallback(() => {
    if (allFilesUploaded) {
      clearFilesUploadingStore();
      setVisible(false);
    } else {
      showCancelConfirmModal();
    }
  }, [allFilesUploaded, clearFilesUploadingStore, showCancelConfirmModal]);

  const modalText = allFilesUploaded ? 'All files uploaded' : 'In progress...';
  const showArrow = !isUploadModalCollapsed && ids.length > 2;

  const toggleShortView = () => {
    setIsListShortView(!isListShortView);
    setIsListShortAnimationInProgress(true);
    setTimeout(() => {
      setIsListShortAnimationInProgress(false);
    }, 150);
  };
  const uploadStatusText = allFilesUploaded ? '✅  All files uploaded' : '⏳  Uploading files. Please wait';

  useEffect(() => {
    if (allFilesUploaded) {
      const isMobile = window.matchMedia('only screen and (max-width: 640px)').matches;
      if (isMobile) {
        const timer = setTimeout(() => {
          handleClose();
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [allFilesUploaded, handleClose]);

  return (
    <div
      className={classNames(
        'files-upload-modal fixed w-[90%] ml-[4%] m-auto bottom-4 p-4  top-auto ease-out inset-0 z-[39] shadow-md sm:rounded-2xl transition-all sm:border sm:border-neutral-200 right-0 left-0 sm:right-20 sm:left-auto',
        getBgClassNames(themeColors, PortalBgType.SURFACE_INNER),
        getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE),
        {
          'sm:w-[470px] sm:p-6': !isUploadModalCollapsed,
          'sm:w-[250px] px-6 py-4': isUploadModalCollapsed,
          'bottom-[-200px]': !visible,
          'bottom-4': visible,
        }
      )}
    >
      <div
        className="sm:hidden font-bold text-sm"
        onClick={() => {
          if (allFilesUploaded) {
            handleClose();
          }
        }}
      >
        {uploadStatusText}
      </div>
      <div className="hidden sm:block">
        <div
          className={classNames('flex justify-end transition-all text-neutral-500', {
            'mb-0': !isUploadModalCollapsed,
            '-mb-4': isUploadModalCollapsed,
          })}
        >
          <div
            className={classNames('cursor-pointer mr-2 transition-all', {
              'rotate-180': !isUploadModalCollapsed,
            })}
            onClick={() => toggleCollapseUploadModal()}
          >
            <Icon icon={IconsMap.ARROW_2_UP} className="w-4 h-4" />
          </div>
          <div className="cursor-pointer" onClick={handleClose}>
            <Icon icon={IconsMap.CLOSE} className="w-4 h-4" />
          </div>
        </div>

        <div
          className={classNames(
            'font-bold mt-1 text-[13px] leading-[16px]',
            getTextClassNames(themeColors, PortalTextType.SURFACE)
          )}
        >
          Attachments
        </div>
        {isUploadModalCollapsed && <FilesUploadProgress themeColors={themeColors} />}
        <div
          className={classNames(
            'rounded flex items-center text-xs transition-all overflow-hidden',
            getBgClassNames(themeColors, PortalBgType.SURFACE_INNER_HOVER),
            getTextClassNames(themeColors, PortalTextType.SURFACE),
            {
              'h-6 py-1 mt-1 pl-2 pr-4': !isUploadModalCollapsed,
              'h-0 p-0 mt-0': isUploadModalCollapsed,
            }
          )}
        >
          <span>{modalText}</span>
          {!allFilesUploaded && (
            <div
              className={classNames(
                'cursor-pointer ml-auto font-bold',
                getTextClassNames(themeColors, PortalTextType.PRIMARY)
              )}
              onClick={showCancelConfirmModal}
            >
              Cancel
            </div>
          )}
        </div>
        <div className="flex items-end">
          <div
            className={classNames('overflow-auto transition-all w-full', {
              'max-h-52 mt-3': !isUploadModalCollapsed && !isListShortView,
              'max-h-24 mt-3': !isUploadModalCollapsed && isListShortView,
              'max-h-0 overflow-hidden mt-0': isUploadModalCollapsed,
              'overflow-hidden': isListShortView || isListShortAnimationInProgress,
            })}
          >
            {ids.map((id) => {
              return <FileProgressListItem key={id} id={id} themeColors={themeColors} themeStyle={themeStyle} />;
            })}
          </div>
          {showArrow && (
            <div
              className={classNames('cursor-pointer transition-all ml-1 text-neutral-500', {
                'rotate-180': !isListShortView,
              })}
              onClick={toggleShortView}
            >
              <Icon icon={IconsMap.ARROW_DOWN_1} className="w-4 h-4" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const FilesUploadProgress: React.FC<{ themeColors: ThemeColorSchemas }> = ({ themeColors }) => {
  const [uploadProgress] = useUnit([$uploadProgress]);
  const percents = Math.round((100 / uploadProgress.bytesCount) * uploadProgress.doneBytesCount);
  return (
    <div>
      <div
        className={classNames(
          'flex text-[10px] leading-[14px]',
          getTextClassNames(themeColors, PortalTextType.SECONDARY)
        )}
      >
        <span className="min-w-7">{percents}%</span>
        <span>
          ({uploadProgress.doneFilesCount}/{uploadProgress.filesCount} files)
        </span>
      </div>
      <div className="flex bg-neutral-200 h-1 w-full rounded overflow-hidden">
        <div style={{ width: `${percents}%` }} className="h-full bg-teal-500 transition-all ease-linear"></div>
      </div>
    </div>
  );
};
